import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import ReactCodeInput from 'react-code-input';
import { Button, Divider } from '@chakra-ui/react';

import { SentCodePaths } from './useLogin';

const CODE_LENGTH = 6;

export interface StepAuthProps {
  start: () => Promise<void>;
  auth: () => Promise<void>;
  setCode: (code: string) => void;
  setError: (error: string | null) => void;
  resetLogin: () => void;
  email: string;
  code: string;
  sentCodePath: SentCodePaths | null;
  isLoading: boolean;
  error: string | null;
  t: any;
}

const checkIsValidCode = (code: string): boolean => {
  return !!(code && /^[0-9]+$/.test(code) && code.length === CODE_LENGTH);
};

const useCountdownResendCode = (countdownSeconds) => {
  const [countdownResendCode, setCountdownResendCode] = useState(0);
  const countdownResendCodeRef = useRef(0);

  useEffect(() => {
    countdownResendCodeRef.current = countdownResendCode;
  }, [countdownResendCode]);

  const decreaseCountdownResendCode = () => {
    setTimeout(() => {
      const countdownResendCodeNew = countdownResendCodeRef.current - 1;
      setCountdownResendCode(countdownResendCodeNew);
      countdownResendCodeNew > 0 && decreaseCountdownResendCode();
    }, 1000);
  };

  const restartCountdownResendCode = () => {
    setCountdownResendCode(countdownSeconds);
    decreaseCountdownResendCode();
  };

  return {
    countdownResendCode,
    restartCountdownResendCode,
  };
};

export const StepAuth: React.FC<StepAuthProps> = ({
  isLoading,
  error,
  code,
  sentCodePath,
  email,
  setCode,
  setError,
  auth,
  start,
  t,
}) => {
  const { countdownResendCode, restartCountdownResendCode } =
    useCountdownResendCode(60);
  const [isValidCode, setIsValidCode] = useState(false);

  const codeInputRef = useRef<ReactCodeInput>(null);

  useEffect(() => {
    !code && restartCountdownResendCode();
  }, []);

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    isValidCode && auth();
  };

  const handleCodeChange = (value) => {
    const code = String(value);
    const isValid = checkIsValidCode(code);

    setError(null);
    setCode(code);
    setIsValidCode(isValid);
  };

  const handleResendCode = () => {
    if (countdownResendCode == 0) {
      clearCodeInput();
      start();
      restartCountdownResendCode();
    }
  };

  const clearCodeInput = () => {
    const item = codeInputRef.current;

    if (item) {
      for (let i = 0; i < item.state?.input?.length; i++) {
        item.state.input[i] = '';
      }
    }
  };

  const inputStyle = {
    borderStyle: 'solid',
    color: 'var(--chakra-colors-gray-800)',
    borderColor: 'var(--chakra-colors-gray-400)',
    borderWidth: '0 0 3px 0',
    backgroundColor: 'var(--chakra-colors-gray-100)',
    fontSize: '19.2px',
    margin: '0 6px',
    padding: '5px 10px',
    fontWeight: 700,
    textAlign: 'center' as const,
    borderRadius: '4px',
    height: '44px',
    lineHeight: '28.8px',
    display: 'inline-block',
    width: '39px',
    boxSizing: 'border-box' as const,
  };

  const inputStyleError = {
    ...inputStyle,
    borderColor: 'var(--error-color)',
    color: 'var(--error-color)',
  };

  return (
    <>
      <Title>{t('login.welcome_back')}</Title>

      {!error ? (
        <Paragraph>
          {sentCodePath === SentCodePaths.email
            ? t('login.auth_intro_email')
            : t('login.auth_intro_email_phone')}
        </Paragraph>
      ) : (
        <Paragraph className="bundleapp-error-tag">{t(error)}</Paragraph>
      )}

      <Divider
        width="24px"
        margin="0 auto 50px"
        borderBottomWidth={2}
        opacity={1}
      />

      <form onSubmit={handleSubmit}>
        <ReactCodeInput
          ref={codeInputRef}
          name="code"
          type="number"
          inputMode="numeric"
          fields={CODE_LENGTH}
          value={code}
          onChange={handleCodeChange}
          inputStyle={inputStyle}
          inputStyleInvalid={inputStyleError}
          isValid={!error}
          disabled={isLoading}
          autoFocus
        />

        <Button
          w="100%"
          isDisabled={isLoading || !!error || !isValidCode}
          isLoading={isLoading}
          type="submit"
          marginTop="30px"
          height="44px"
          maxWidth="280px"
          className="bundle-button button--primary"
        >
          {t('login.continue')}
        </Button>

        {email ? (
          <Button
            w="100%"
            isDisabled={isLoading || countdownResendCode > 0}
            marginTop="10px"
            onClick={handleResendCode}
            variant="ghost"
            height="44px"
            maxWidth="280px"
          >
            {t('login.resend_code')}
            {countdownResendCode > 0 ? (
              <Countdown>({countdownResendCode})</Countdown>
            ) : null}
          </Button>
        ) : null}
      </form>
    </>
  );
};

const Title = styled.h2`
  margin: 24px auto;
  font-size: 32px;
  font-weight: 600;
`;

const Paragraph = styled.h3`
  margin: 0 auto 40px;
`;

const Countdown = styled.span`
  margin-left: 3px;
`;
